import { TitleCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { AwsS3MetadataDto, FileAdminDto, FileLocalhostMetadataDto, SmsStatus } from '@workspace/data-types';
import {
  AvatarAuraComponent,
  DetailItem,
  DetailsPanelAuraComponent,
  FileSizeDisplayPipe,
  LuxonDatePipe,
} from '@workspace/ng-aura';
import {
  FilePurposeDisplayPipe,
  StateReasonDisplayPipe,
  StateStatusDisplayPipe,
} from '@workspace/ng-data-access';

export interface ThumbnailDetails {
  key: string;
  columns: DetailItem[][];
  url: string;
}

@Component({
  selector: 'core-file-details',
  templateUrl: './file-details.component.html',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatButtonModule,
    MatDividerModule,
    MatIconModule,
    MatTooltipModule,
    LuxonDatePipe,
    FilePurposeDisplayPipe,
    RouterModule,
    TitleCasePipe,
    AvatarAuraComponent,
    RouterModule,
    DetailsPanelAuraComponent,
  ],
})
export class FileDetailsCoreComponent {
  file = input.required<FileAdminDto>();
  smsStatus = SmsStatus;
  fileSources = signal<DetailItem[][]>([[], []]);
  fileDetails = signal<DetailItem[][]>([[], []]);
  fileThumbnails = signal<ThumbnailDetails[]>([]);

  thumbnails = computed(() => {
    const th = this.file().thumbnail;
    if (!th) return [];

    return Object.entries(th).map(([key, value]) => ({
      name: key, // small / medium
      data: value, // actual thumbnail object
    }));
  });

  datePipe = new LuxonDatePipe();
  sizePipe = new FileSizeDisplayPipe();
  stateStatusPipe = new StateStatusDisplayPipe();
  stateReasonPipe = new StateReasonDisplayPipe();

  constructor() {
    effect(() => {
      let metadata: AwsS3MetadataDto | FileLocalhostMetadataDto | undefined;
      if (this.file().metadata?.aws) {
        metadata = this.file().metadata.aws;
      } else if (this.file().metadata?.localhost) {
        metadata = this.file().metadata.localhost;
      }

      const rightDetails: DetailItem[] = [];
      const leftDetails: DetailItem[] = [
        {
          label: 'Statut',
          value: this.file().state.status,
          pipe: this.stateStatusPipe,
        },
      ];

      if (this.file().state.deactivatedAt) {
        leftDetails.push({
          label: 'Désactivé le',
          value: this.file().state.deactivatedAt,
          pipe: this.datePipe,
        });
      }
      if (this.file().state.deletedAt) {
        leftDetails.push({
          label: 'Supprimé le',
          value: this.file().state.deletedAt,
          pipe: this.datePipe,
        });
      }
      if (this.file().state.reason) {
        rightDetails.push({
          label: 'Raison du statut',
          value: this.file().state.reason,
          pipe: this.stateReasonPipe,
        });
      }

      this.fileDetails.set([leftDetails, rightDetails]);

      this.fileSources.set([
        [
          { label: 'Nom du fichier', value: this.file().filename },
          { label: 'Mimetype', value: this.file().mimeType },
          { label: 'Taille', value: this.file().size, pipe: this.sizePipe },
          {
            label: 'Dimensions',
            value: `${metadata?.dimension?.width} x ${metadata?.dimension?.height}`,
          },
        ],
        [
          { label: 'Bucket', value: metadata?.bucket },
          { label: 'URL signé', value: this.file().url },
          { label: 'eTag', value: metadata?.eTag },
          { label: 'Tentatives', value: metadata?.attempts },
        ],
      ]);

      const _thumbnailsDetails: ThumbnailDetails[] = [];

      for (const thumbnail of this.thumbnails()) {
        _thumbnailsDetails.push({
          key: thumbnail.name,
          url: thumbnail.data.url,
          columns: [
            [
              { label: 'Nom du fichier', value: thumbnail.data.filename },
              {
                label: 'Taille',
                value: thumbnail.data.size,
                pipe: this.sizePipe,
              },
              {
                label: 'Dimensions',
                value: `${thumbnail.data.dimension.width} x ${thumbnail.data.dimension.height}`,
              },
            ],
            [
              { label: 'URL signé', value: thumbnail.data.url },
              { label: 'eTag', value: thumbnail.data.eTag },
            ],
          ],
        });
      }
      this.fileThumbnails.set(_thumbnailsDetails);
    });
  }
}
